import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, LinearProgress, Alert, Button } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useVideoProgress } from '../hooks/useTraining';

interface TrainingVideoPlayerProps {
  courseId: string;
  videoUrl: string;
  captionUrl?: string | null;
  transcriptText?: string | null;
  currentPosition?: number;
  onComplete?: () => void;
}

// Helper to extract YouTube video ID
function getYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
}

export default function TrainingVideoPlayer({
  courseId,
  videoUrl,
  captionUrl,
  transcriptText,
  currentPosition = 0,
  onComplete,
}: TrainingVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [duration, setDuration] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(currentPosition);
  
  const { updatePosition, saveProgress } = useVideoProgress(courseId, duration);
  
  const youtubeVideoId = getYouTubeVideoId(videoUrl);
  const isYouTube = !!youtubeVideoId;

  // Manual completion is available for every video type so caregivers can always
  // advance to the acknowledgment/quiz step — YouTube (no "ended" event), direct
  // HTML5 video (the browser may not fire "ended" if the user scrubs), and
  // content-only courses with no video at all.
  const handleMarkComplete = () => {
    if (hasCompleted) return;
    setHasCompleted(true);
    const completedSeconds = !isYouTube && duration > 0 ? duration : 1;
    saveProgress(completedSeconds);
    onComplete?.();
  };

  useEffect(() => {
    // Skip video event handling for YouTube videos
    if (isYouTube) {
      // For YouTube, we'll use a simplified progress tracking
      setDuration(1); // Placeholder duration for YouTube
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    // Set initial position
    if (currentPosition > 0) {
      video.currentTime = currentPosition;
    }

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      updatePosition(video.currentTime);
    };

    const handleEnded = () => {
      if (!hasCompleted) {
        setHasCompleted(true);
        saveProgress(video.duration);
        onComplete?.();
      }
    };

    const handleError = () => {
      setError('Failed to load video. Please try again.');
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
    };
  }, [currentPosition, hasCompleted, onComplete, updatePosition, saveProgress, duration, isYouTube]);

  const progressPercentage = duration > 0
    ? (currentTime / duration) * 100
    : 0;

  if (!videoUrl) {
    return (
      <Box p={4}>
        <Alert severity="info" sx={{ mb: 2 }}>
          This course has no video content. Review the course details above, then
          mark it complete to continue.
        </Alert>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
          onClick={handleMarkComplete}
          disabled={hasCompleted}
          fullWidth
        >
          {hasCompleted ? 'Completed ✓' : 'Mark as Complete'}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Video Player */}
      <Box
        sx={{
          position: 'relative',
          paddingTop: '56.25%', // 16:9 aspect ratio
          bgcolor: '#000',
        }}
      >
        {isYouTube ? (
          // YouTube iframe
          <iframe
            ref={iframeRef}
            src={`https://www.youtube.com/embed/${youtubeVideoId}?start=${Math.floor(currentPosition)}&enablejsapi=1`}
            title="Training Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        ) : (
          // HTML5 video for direct video files
          <video
            ref={videoRef}
            controls
            controlsList="nodownload"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            {captionUrl ? (
              <track
                kind="captions"
                src={captionUrl}
                srcLang="en"
                label="English captions"
                default
              />
            ) : null}
            Your browser does not support the video tag.
          </video>
        )}
      </Box>

      {/* Progress Info */}
      <Box p={2} bgcolor="background.paper">
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            Course Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isYouTube ? 'Watch the video' : `${Math.round(progressPercentage)}% completed`}
          </Typography>
        </Box>
        {!isYouTube && <LinearProgress variant="determinate" value={progressPercentage} />}

        {transcriptText ? (
          <Box
            component="details"
            sx={{ mt: 2 }}
            data-testid="training-video-transcript"
          >
            <Typography
              component="summary"
              variant="subtitle2"
              sx={{ cursor: 'pointer', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
            >
              View transcript
            </Typography>
            <Typography variant="body2" component="p" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {transcriptText}
            </Typography>
          </Box>
        ) : null}
        
        {!isYouTube && currentPosition > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Resuming from {formatTime(currentPosition)}
          </Typography>
        )}

        <Box mt={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Watch the complete video, then click "Mark as Complete" below to continue.
          </Alert>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={handleMarkComplete}
            disabled={hasCompleted}
            fullWidth
          >
            {hasCompleted ? 'Video Completed ✓' : 'Mark as Complete'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// Helper function to format seconds as MM:SS
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

